import { WaitlistForm } from './WaitlistForm';
import { WaitlistCounter } from './WaitlistCounter';
import { VERTICALS, verticalCopy } from '@/lib/verticals';
import { useT, useRaw } from '@/lib/i18n';

export const Hero = () => {
  const t = useT();
  const raw = useRaw();
  return (
    <section
      data-testid="site-hero"
      className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-20 md:pb-32"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="om-anim-halo absolute -top-24 left-[-8%] h-[420px] w-[420px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(139, 92, 246, 0.35), transparent 70%)' }}
        />
        <div
          className="om-anim-halo absolute top-[10%] right-[-6%] h-[520px] w-[520px] rounded-full blur-3xl om-d-200"
          style={{ background: 'radial-gradient(closest-side, rgba(16, 185, 129, 0.28), transparent 70%)' }}
        />
        <div
          className="om-anim-halo absolute bottom-[-10%] left-[30%] h-[360px] w-[360px] rounded-full blur-3xl om-d-400"
          style={{ background: 'radial-gradient(closest-side, rgba(249, 115, 22, 0.24), transparent 70%)' }}
        />
      </div>

      <div className="om-container">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-6 xl:col-span-6">
            <div className="om-anim-fade-up">
              <span className="om-eyebrow" data-testid="hero-eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t('hero.eyebrow')}
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="om-h1 om-anim-fade-up om-d-100 mt-6 text-[44px] sm:text-[64px] lg:text-[72px] xl:text-[80px] text-[var(--om-ink)]"
            >
              {t('hero.title_l1')}
              <br />
              {t('hero.title_l2')}
              <br />
              <span
                style={{
                  backgroundImage:
                    'linear-gradient(120deg, #10B981 0%, #0EA5E9 25%, #8B5CF6 55%, #F43F5E 80%, #F97316 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('hero.title_l3')}
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="om-anim-fade-up om-d-200 mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[var(--om-muted)]"
            >
              {t('hero.subtitle')}
            </p>

            <div className="om-anim-fade-up om-d-300 mt-8" id="waitlist-hero">
              <WaitlistForm source="hero" variant="hero" />
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[var(--om-muted-2)]" data-testid="hero-microcopy">
                <span className="inline-flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="#10B981" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('waitlist.no_spam_short')}
                </span>
                <WaitlistCounter />
              </div>
            </div>

            <div className="om-anim-fade-up om-d-400 mt-10">
              <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                {t('hero.universes_label')}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {VERTICALS.map((v) => {
                  const vc = verticalCopy(raw, v.code);
                  return (
                    <span
                      key={v.code}
                      data-testid={`hero-vertical-chip-${v.code}`}
                      className="inline-flex items-center gap-2 rounded-full border border-[var(--om-border)] bg-white/70 px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] backdrop-blur transition-transform duration-200 hover:-translate-y-[1px]"
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: v.color, boxShadow: `0 0 0 4px ${v.tint}` }}
                      />
                      {vc.short}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 xl:col-span-6">
            <div className="om-anim-fade-up om-d-200 relative">
              <img
                src="/brand/hero.png"
                alt={t('hero.illustration_alt')}
                width="1280"
                height="720"
                loading="eager"
                fetchPriority="high"
                data-testid="hero-illustration"
                className="om-anim-float w-full h-auto select-none"
                draggable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
